import { Icon } from '@examdojo/core/icon';
import { ImageEditingType, QuestionEditingOptions } from '@examdojo/models/question-editor';
import {
  DerivativeQuestionReviewType,
  MarkschemeDisplayElement,
  QUESTION_REVIEW_TYPE_TO_REVIEW_PERMISSION,
  ReviewPermission,
} from '@examdojo/review';

export interface DerivativeQuestionReviewConfig extends QuestionEditingOptions {
  displayName: string;
  icon: Icon;
  urlPart: DerivativeQuestionReviewType;
  isSkippable: boolean;
  canStoreInBraintrust: boolean;
  allowJsonEditor: boolean;
  permission: ReviewPermission;
  showPreviousFeedback: DerivativeQuestionReviewType[];
  displayMarkschemeElements?: MarkschemeDisplayElement[];
  hideMarkschemeMetadata?: boolean; // hide markscheme metadata (e.g. markscheme topics, id mark code)
  allowMarkschemeStructureEditing?: boolean;
  allowMcqStructureEditing?: boolean;
  showReferenceQuestion: boolean;
}

export const DERIVATIVE_QUESTION_REVIEW_CONFIG: Record<DerivativeQuestionReviewType, DerivativeQuestionReviewConfig> = {
  [DerivativeQuestionReviewType.PreScreening]: {
    displayName: 'Pre-screening',
    icon: 'magnifying-glass-waveform',
    urlPart: DerivativeQuestionReviewType.PreScreening,
    isSkippable: true,
    allowImageEditing: [],
    allowQuestionEditing: true,
    allowMarkschemeEditing: true,
    allowMcqEditing: true,
    allowMarkschemeStructureEditing: false,
    allowMcqStructureEditing: false,
    canStoreInBraintrust: true,
    allowJsonEditor: false,
    permission: QUESTION_REVIEW_TYPE_TO_REVIEW_PERMISSION[DerivativeQuestionReviewType.PreScreening],
    showPreviousFeedback: [],
    displayMarkschemeElements: ['all_mark_groups', 'solution_paths'],
    hideMarkschemeMetadata: true,
    hideBadges: false,
    showReferenceQuestion: true,
    hideImageAndTableMetadata: true,
  },
  [DerivativeQuestionReviewType.ExpertPrescreening]: {
    displayName: 'Pre-screening +',
    icon: 'user-secret',
    urlPart: DerivativeQuestionReviewType.ExpertPrescreening,
    isSkippable: false,
    allowImageEditing: [],
    allowQuestionEditing: true,
    allowMarkschemeEditing: true,
    allowMcqEditing: true,
    allowMarkschemeStructureEditing: false,
    allowMcqStructureEditing: false,
    canStoreInBraintrust: true,
    allowJsonEditor: false,
    permission: QUESTION_REVIEW_TYPE_TO_REVIEW_PERMISSION[DerivativeQuestionReviewType.ExpertPrescreening],
    displayMarkschemeElements: ['all_mark_groups', 'solution_paths'],
    hideMarkschemeMetadata: true,
    showPreviousFeedback: [DerivativeQuestionReviewType.PreScreening, DerivativeQuestionReviewType.ExpertPrescreening],
    hideBadges: false,
    showReferenceQuestion: true,
    hideImageAndTableMetadata: true,
  },
  [DerivativeQuestionReviewType.ImageReview]: {
    displayName: 'Images',
    icon: 'image',
    urlPart: DerivativeQuestionReviewType.ImageReview,
    isSkippable: true,
    allowImageEditing: [ImageEditingType.Generated],
    allowQuestionEditing: false,
    allowMarkschemeEditing: false,
    allowMcqEditing: false,
    allowMarkschemeStructureEditing: false,
    allowMcqStructureEditing: false,
    canStoreInBraintrust: true,
    allowJsonEditor: false,
    permission: QUESTION_REVIEW_TYPE_TO_REVIEW_PERMISSION[DerivativeQuestionReviewType.ImageReview],
    displayMarkschemeElements: ['solution_paths'],
    hideMarkschemeMetadata: true,
    showPreviousFeedback: [],
    hideBadges: false,
    showReferenceQuestion: true,
    hideImageAndTableMetadata: true,
  },
  [DerivativeQuestionReviewType.ExpertImageReview]: {
    displayName: 'Images +',
    icon: 'image-polaroid',
    urlPart: DerivativeQuestionReviewType.ExpertImageReview,
    isSkippable: false,
    allowImageEditing: [ImageEditingType.Generated],
    allowQuestionEditing: false,
    allowMarkschemeEditing: false,
    allowMcqEditing: false,
    allowMarkschemeStructureEditing: false,
    allowMcqStructureEditing: false,
    canStoreInBraintrust: true,
    allowJsonEditor: false,
    permission: QUESTION_REVIEW_TYPE_TO_REVIEW_PERMISSION[DerivativeQuestionReviewType.ExpertImageReview],
    displayMarkschemeElements: ['solution_paths'],
    hideMarkschemeMetadata: true,
    showPreviousFeedback: [DerivativeQuestionReviewType.ImageReview, DerivativeQuestionReviewType.ExpertImageReview],
    hideBadges: false,
    showReferenceQuestion: true,
    hideImageAndTableMetadata: true,
  },
  [DerivativeQuestionReviewType.HumanReview]: {
    displayName: 'Deep review',
    icon: 'square-question',
    urlPart: DerivativeQuestionReviewType.HumanReview,
    isSkippable: true,
    allowImageEditing: [],
    allowQuestionEditing: false,
    allowMarkschemeEditing: false,
    allowMcqEditing: false,
    allowMarkschemeStructureEditing: false,
    allowMcqStructureEditing: false,
    canStoreInBraintrust: false,
    allowJsonEditor: false,
    permission: QUESTION_REVIEW_TYPE_TO_REVIEW_PERMISSION[DerivativeQuestionReviewType.HumanReview],
    showPreviousFeedback: [],
    displayMarkschemeElements: ['solution_paths'],
    hideMarkschemeMetadata: true,
    hideBadges: true,
    showReferenceQuestion: false,
    hideImageAndTableMetadata: true,
  },
  [DerivativeQuestionReviewType.TieBreak]: {
    displayName: 'Tie-break',
    icon: 'yin-yang',
    urlPart: DerivativeQuestionReviewType.TieBreak,
    isSkippable: false,
    allowImageEditing: [],
    allowQuestionEditing: false,
    allowMarkschemeEditing: false,
    allowMcqEditing: false,
    allowMarkschemeStructureEditing: false,
    allowMcqStructureEditing: false,
    canStoreInBraintrust: false,
    allowJsonEditor: false,
    permission: QUESTION_REVIEW_TYPE_TO_REVIEW_PERMISSION[DerivativeQuestionReviewType.TieBreak],
    showPreviousFeedback: [DerivativeQuestionReviewType.HumanReview, DerivativeQuestionReviewType.TieBreak],
    displayMarkschemeElements: ['solution_paths'],
    hideMarkschemeMetadata: true,
    hideBadges: true,
    showReferenceQuestion: true,
    hideImageAndTableMetadata: true,
  },
};

export function getQuestionReviewConfig(mode: DerivativeQuestionReviewType): DerivativeQuestionReviewConfig {
  return DERIVATIVE_QUESTION_REVIEW_CONFIG[mode];
}

export function getAllQuestionReviewConfigs(): DerivativeQuestionReviewConfig[] {
  return Object.values(DERIVATIVE_QUESTION_REVIEW_CONFIG);
}
